
import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { EmailTemplatesList } from '@/components/admin/settings/EmailTemplatesList';
import { StableTabContainer } from '@/components/admin/settings/email-editor/StableTabContainer';
import { EmailPageLayout } from '@/components/admin/settings/email-editor/EmailPageLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function EmailTemplatesPage() {
  const [hasError, setHasError] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);

  // Control initial page transition
  useEffect(() => {
    // Start with animation
    setIsAnimating(true);
    // End animation after a short delay
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  const handleReset = useCallback(() => {
    setIsAnimating(true);
    setHasError(false);
    toast.success("Retrying to load templates");
    // End animation after a short delay
    setTimeout(() => setIsAnimating(false), 300);
  }, []);

  return (
    <AdminLayout>
      <div className="w-full overflow-visible">
        <EmailPageLayout>
          <div 
            className="email-templates-content transition-opacity duration-300 ease-in-out w-full"
            style={{
              minHeight: '600px',
              width: '100%',
              position: 'relative',
              opacity: isAnimating ? 0.7 : 1,
              overflow: 'visible',
            }}
          >
            <ErrorBoundary
              fallback={
                <Alert variant="destructive" className="mb-6 max-w-[1200px] mx-auto">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Something went wrong</AlertTitle>
                  <AlertDescription>
                    An error occurred while loading email templates. This may be due to insufficient browser resources.
                    Try refreshing the page or clearing your browser cache.
                  </AlertDescription>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2" 
                    onClick={handleReset}
                  >
                    Try again
                  </Button>
                </Alert>
              }
              onError={(error) => {
                console.error("EmailTemplates error:", error);
                setHasError(true);
              }}
            >
              <StableTabContainer 
                className="email-templates-container"
                height="auto"
                style={{ 
                  minHeight: '600px',
                  width: '100%',
                  display: 'flex', 
                  flexDirection: 'column',
                  overflow: 'visible',
                }}
              >
                <EmailTemplatesList />
              </StableTabContainer>
            </ErrorBoundary>
          </div>
        </EmailPageLayout>
      </div>
    </AdminLayout>
  );
}
